import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { normalizarImporte } from '../utils/formats';

var config = process.env;

// Insumos del submódulo Cafetería (ENI-82). Dos niveles de stock:
// paquetes cerrados (product.stock, se reponen por /restocking) + remanente del
// paquete abierto en unidad base (se descuenta por receta al vender).
const CATEGORY_LABELS = {
    cafeteria: 'Cafetería',
    cleaning: 'Limpieza',
    packaging: 'Packaging',
    other: 'Otros',
};

const UNIT_LABELS = { g: 'g', ml: 'ml', unit: 'u' };

// "Abrir paquete" se ofrece cuando al paquete en uso le queda <= este ratio del paquete
// (o ya se agotó): permite abrir el siguiente antes de llegar a 0. Default 10%.
const OPEN_LOW_RATIO = 0.10;

// Los totales en unidad base salen de multiplicar DECIMAL(_,2): 5×33.33+33.33 puede
// imprimir 199.98000000000002. Se recorta al mostrar (no al comparar).
function round2(value) {
    return Math.round(Number(value) * 100) / 100;
}

function headers() {
    return {
        'Content-Type': 'application/json',
        'access-token': localStorage.getItem('token'),
    };
}

class CafeteriaInsumosPage extends Component {
    constructor() {
        super();
        this.state = {
            checked: false,
            role: 0,
            items: [],
            loading: true,
            categoryFilter: '',
            openingCode: null, // insumo cuyo "Abrir paquete" está en curso (anti doble-click)
            // Modal de ajuste manual
            adjustItem: null,
            adjustPackagesDelta: '',
            adjustNewRemainder: '',
            adjustNotes: '',
            adjustSaving: false,
            // Modal de reposición
            restockOpen: false,
            providers: [],
            restockProvider: '',
            restockInvoice: '',
            restockInvoiceTotal: '',
            restockLines: {}, // code -> { quantity, cost }
            restockSaving: false,
            // Filtros de la lista de insumos dentro del modal de reposición (solo afectan qué
            // filas se muestran; los seleccionados en restockLines persisten aunque se filtren).
            restockFilterName: '',
            restockFilterCategory: '',
            // Modal de movimientos
            movementsItem: null,
            movements: [],
            movementsLoading: false,
        };
    }

    async componentDidMount() {
        const role = await validateSession();
        this.setState({ checked: true, role });
        this.fetchItems();
        this.fetchProviders();
    }

    fetchItems = () => {
        this.setState({ loading: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/supplies', { method: 'GET', headers: headers() })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    if (data.error === 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                    this.setState({ loading: false });
                } else {
                    this.setState({ items: data.data || [], loading: false });
                }
            })
            .catch(() => {
                alert('Error de red al cargar los insumos.');
                this.setState({ loading: false });
            });
    }

    fetchProviders = () => {
        // ENI-92: en la reposición de insumos solo mostramos proveedores de cafetería
        // (el backend suma los 'general' sin clasificar), no los de juegos/limpieza.
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'provider/product?category=cafeteria', { method: 'GET', headers: headers() })
            .then((results) => results.json())
            .then((data) => {
                if (!(data.error > 0)) {
                    this.setState({ providers: data.data || [] });
                }
            })
            .catch(() => { /* proveedores opcionales: la reposición valida antes de enviar */ });
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    // ===== Ajuste manual =====

    openAdjust = (item) => {
        this.setState({ adjustItem: item, adjustPackagesDelta: '', adjustNewRemainder: '', adjustNotes: '', adjustSaving: false });
    }

    // ¿Se puede "abrir un paquete nuevo"? Solo consumibles fraccionables (package_size>1: café por
    // gramo, latas de una caja) con al menos un paquete cerrado, cuando al paquete en uso le queda
    // POCO (<= OPEN_LOW_RATIO del paquete) o ya se agotó. Así se puede abrir el siguiente antes de
    // llegar a 0, sin tirar el remanente (se acumula). Los envasados (package_size=1) se venden
    // enteros: no tienen apertura.
    canOpenPackage = (item) => (
        Number(item.isConsumable) === 1
        && Number(item.packageSize) > 1
        && Number(item.stock) >= 1
        && Number(item.openRemainder) <= Number(item.packageSize) * OPEN_LOW_RATIO
    );

    // Apertura manual: baja 1 paquete cerrado y suma package_size al remanente en uso (acumula: el
    // remanente puede superar un paquete si se abre antes de agotar el actual). Endpoint dedicado
    // (no el de ajuste, que topea a un paquete). Queda asentado como movimiento "Apertura de paquete".
    openPackageNew = (item) => {
        if (this.state.openingCode != null) {
            return; // evitar doble apertura mientras una está en curso
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        const total = Number(item.openRemainder) + Number(item.packageSize);
        if (!window.confirm(`¿Abrir un paquete nuevo de "${item.name}"? Suma ${item.packageSize} ${unit} a lo que queda en uso (total ${total} ${unit}) y baja 1 paquete cerrado.`)) {
            return;
        }
        this.setState({ openingCode: item.code });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/supplies/${encodeURIComponent(item.code)}/open`, {
            method: 'POST', headers: headers(),
        })
            .then((results) => results.json())
            .then((data) => {
                this.setState({ openingCode: null });
                if (data.error > 0) {
                    alert(data.message);
                    return;
                }
                this.fetchItems();
            })
            .catch(() => {
                this.setState({ openingCode: null });
                alert('Error de red al abrir el paquete.');
            });
    }

    saveAdjust = () => {
        const { adjustItem, adjustPackagesDelta, adjustNewRemainder, adjustNotes } = this.state;
        const body = {};
        // ENI-189: se espejan las reglas del backend para que el operador reciba un mensaje que
        // se entienda, en vez del 6103 genérico ("Ajuste inválido: falta packagesDelta o
        // newRemainder"), que no dice cuál de las dos cosas está mal.
        if (adjustPackagesDelta !== '' && Number(adjustPackagesDelta) !== 0) {
            if (!Number.isInteger(Number(adjustPackagesDelta))) {
                alert('Los paquetes a sumar o restar tienen que ser un número entero (ej: -1, 2).');
                return;
            }
            body.packagesDelta = Number(adjustPackagesDelta);
        }
        if (adjustNewRemainder !== '') {
            const remainder = Number(adjustNewRemainder);
            const max = Number(adjustItem.packageSize);
            if (!Number.isFinite(remainder)) {
                alert('Lo que queda en el paquete en uso tiene que ser un número.');
                return;
            }
            if (remainder < 0 || (max > 0 && remainder > max)) {
                alert(`Lo que queda en el paquete tiene que estar entre 0 y ${max} (el paquete entero).`);
                return;
            }
            body.newRemainder = remainder;
        }
        if (body.packagesDelta == null && body.newRemainder == null) {
            alert('Completá al menos uno de los dos campos: los paquetes a sumar/restar o lo que queda en el paquete en uso.');
            return;
        }
        body.notes = adjustNotes;
        this.setState({ adjustSaving: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/supplies/${encodeURIComponent(adjustItem.code)}/adjust`, {
            method: 'POST', headers: headers(), body: JSON.stringify(body),
        })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ adjustSaving: false });
                } else {
                    this.setState({ adjustItem: null });
                    this.fetchItems();
                }
            })
            .catch(() => {
                alert('Error de red al guardar el ajuste.');
                this.setState({ adjustSaving: false });
            });
    }

    // ===== Reposición (reusa POST /restocking, productType=5 — D-07/D-13) =====

    openRestock = () => {
        this.setState({ restockOpen: true, restockLines: {}, restockInvoice: '', restockInvoiceTotal: '', restockSaving: false, restockFilterName: '', restockFilterCategory: '' });
    }

    toggleRestockLine = (item) => {
        const lines = { ...this.state.restockLines };
        if (lines[item.code]) {
            delete lines[item.code];
        } else {
            lines[item.code] = { quantity: 1, cost: item.cost != null ? item.cost : 0 };
        }
        this.setState({ restockLines: lines });
    }

    updateRestockLine = (code, field, value) => {
        const lines = { ...this.state.restockLines };
        if (lines[code]) {
            lines[code] = { ...lines[code], [field]: value };
            this.setState({ restockLines: lines });
        }
    }

    saveRestock = () => {
        const { restockProvider, restockInvoice, restockInvoiceTotal, restockLines, items } = this.state;
        const codes = Object.keys(restockLines);
        if (restockProvider === '') {
            alert('Elegí un proveedor.');
            return;
        }
        if (codes.length === 0) {
            alert('Elegí al menos un insumo.');
            return;
        }
        if (restockInvoice.trim() === '') {
            alert('Indicá el ID de factura.');
            return;
        }
        // ENI-94: el total de la factura es obligatorio (> 0) para confirmar la reposición.
        if (!(normalizarImporte(restockInvoiceTotal) > 0)) {
            alert('Indicá el total de la factura, mayor a 0 y SIN puntos de miles.\n\nEjemplo: 12500.50 (no 12.500)');
            return;
        }
        const products = codes.map((code) => {
            const item = items.find((i) => String(i.code) === String(code));
            const line = restockLines[code];
            return {
                prefix: item.prefix,
                code: item.code,
                name: item.name,
                quantity: parseInt(line.quantity, 10),
                price: 0,
                cost: parseFloat(line.cost),
                discount: 0,
                productType: 5,
            };
        });
        if (products.some((p) => !(p.quantity > 0) || !(p.cost >= 0) || Number.isNaN(p.cost))) {
            alert('Revisá cantidades (> 0) y costos (>= 0).');
            return;
        }
        this.setState({ restockSaving: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'restocking', {
            method: 'POST',
            headers: headers(),
            body: JSON.stringify({
                date: new Date().toISOString(),
                provider: parseInt(restockProvider, 10),
                products,
                // ENI-32: el estado de pago ya no se elige acá — vive en la factura del proveedor
                // y sólo lo mueve `pagarFactura`, que es quien asienta la caja. Se manda el 2 fijo
                // ("pendiente") para la columna deprecada, que sólo existe por si hay rollback.
                paymentStatus: 2,
                invoice: restockInvoice,
                invoiceLink: '',
                // Ni `parseInt` ("12.500" -> 12) ni `Number` ("12.500" -> 12,5): ese número es el
                // que se le paga al proveedor, así que un importe ambiguo se rechaza más arriba.
                invoiceTotal: normalizarImporte(restockInvoiceTotal),
            }),
        })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ restockSaving: false });
                } else {
                    this.setState({ restockOpen: false });
                    this.fetchItems();
                }
            })
            .catch(() => {
                alert('Error de red al registrar la reposición.');
                this.setState({ restockSaving: false });
            });
    }

    // ===== Movimientos (ledger) =====

    openMovements = (item) => {
        // Secuenciación: si el usuario cambia de insumo rápido, solo la última request
        // pintada gana (evita que una respuesta lenta de A pise el ledger de B).
        const reqId = (this.movementsReqId || 0) + 1;
        this.movementsReqId = reqId;
        this.setState({ movementsItem: item, movements: [], movementsLoading: true });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/supplies/movements?code=${encodeURIComponent(item.code)}`, {
            method: 'GET', headers: headers(),
        })
            .then((results) => results.json())
            .then((data) => {
                if (reqId !== this.movementsReqId) {
                    return; // respuesta stale de un insumo ya no seleccionado
                }
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ movementsLoading: false });
                } else {
                    this.setState({ movements: data.data || [], movementsLoading: false });
                }
            })
            .catch(() => {
                if (reqId !== this.movementsReqId) {
                    return;
                }
                alert('Error de red al cargar los movimientos.');
                this.setState({ movementsLoading: false });
            });
    }

    // ===== Render =====

    remainderLabel = (item) => {
        if (Number(item.isConsumable) !== 1) {
            return '—';
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        return `${item.openRemainder} ${unit}`;
    }

    // ENI-229: lo que decide "Bajo mínimo" es la disponibilidad TOTAL (cerrados + lo que
    // queda en el paquete abierto), y la pantalla no la mostraba en ningún lado: se veía
    // "5 / mín 6" y el aviso parecía arbitrario. Se muestra el total y el mínimo en la
    // MISMA unidad, para que el operador pueda hacer la cuenta con los ojos.
    availableLabel = (item) => {
        if (Number(item.isConsumable) !== 1) {
            return `${item.stock}`;
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        return `${round2(item.totalInBaseUnit)} ${unit}`;
    }

    minLabel = (item) => {
        if (Number(item.isConsumable) !== 1) {
            return `${item.minStock}`;
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        return `${round2(Number(item.minStock) * Number(item.packageSize))} ${unit}`;
    }

    drawRow = (item) => {
        // El rojo queda SOLO para lo que impide operar: un remanente negativo (el dato
        // está roto, se consumió más de lo que había) o quedarse sin nada. "Bajo mínimo"
        // es una señal de REPOSICIÓN — hay stock y se puede vender — así que se comunica
        // con el badge ámbar y no pintando la fila (antes eran el mismo rojo y el operador
        // leía "no tengo stock" teniendo de sobra).
        const blocked = item.negativeRemainder || item.outOfStock;
        return (
            <tr key={item.code} className={blocked ? 'table-danger' : ''}>
                <td>{item.name}</td>
                <td>{CATEGORY_LABELS[item.category] || item.category}</td>
                <td className="text-center">{item.stock}</td>
                <td className="text-center">{this.remainderLabel(item)}</td>
                <td className="text-center">
                    {this.availableLabel(item)}
                    {item.minStock != null && (<div className="text-muted small">mín {this.minLabel(item)}</div>)}
                </td>
                <td className="text-center">
                    {item.negativeRemainder && (<span className="badge badge-danger mr-1">Remanente negativo</span>)}
                    {item.outOfStock
                        ? (<span className="badge badge-danger">Sin stock</span>)
                        : (item.lowStock && (<span className="badge badge-warning">Bajo mínimo</span>))}
                </td>
                <td className="text-right">
                    {this.canOpenPackage(item) && (
                        <button className="btn btn-sm btn-outline-primary mr-1" disabled={this.state.openingCode != null} onClick={() => this.openPackageNew(item)}>Abrir paquete</button>
                    )}
                    <button className="btn btn-sm btn-white mr-1" onClick={() => navigateTo(`/editCafeteriaInsumo?code=${item.code}`)}>Editar</button>
                    <button className="btn btn-sm btn-white mr-1" onClick={() => this.openAdjust(item)}>Ajustar</button>
                    <button className="btn btn-sm btn-white" onClick={() => this.openMovements(item)}>Movimientos</button>
                </td>
            </tr>
        );
    }

    drawAdjustModal = () => {
        const item = this.state.adjustItem;
        if (!item) {
            return null;
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        return (
            <div>
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Ajustar — {item.name}</h5>
                                <button type="button" className="close" disabled={this.state.adjustSaving} onClick={() => this.setState({ adjustItem: null })}><span>&times;</span></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-muted mb-2">
                                    Hoy: <strong>{item.stock}</strong> paquetes cerrados{Number(item.isConsumable) === 1 ? (<span> + <strong>{item.openRemainder} {unit}</strong> en el paquete en uso</span>) : null}.
                                </p>
                                {/* ENI-189: los dos campos tienen semántica OPUESTA (uno es una diferencia,
                                    el otro un total que reemplaza) y antes nada lo decía. */}
                                <p className="text-muted mb-3">Completá al menos uno de los dos campos. El que dejes vacío queda como está.</p>
                                <div className="form-group">
                                    <label>Paquetes cerrados: cuántos sumar o restar</label>
                                    <input type="number" step="1" name="adjustPackagesDelta" className="form-control" value={this.state.adjustPackagesDelta} onChange={this.handleChange} placeholder="ej: -1" />
                                    <small className="form-text text-muted">
                                        Es la diferencia, no el total. Poné -1 para sacar un paquete o 2 para sumar dos. Hoy hay {item.stock}.
                                    </small>
                                </div>
                                {Number(item.isConsumable) === 1 && (
                                    <div className="form-group">
                                        <label>Lo que queda en el paquete en uso ({unit})</label>
                                        <input type="number" min="0" max={item.packageSize} step="any" name="adjustNewRemainder" className="form-control" value={this.state.adjustNewRemainder} onChange={this.handleChange} placeholder={`0 a ${item.packageSize} — vacío = no cambia`} />
                                        <small className="form-text text-muted">
                                            Es el total que queda, no la diferencia: reemplaza los {item.openRemainder} {unit} de ahora. Tiene que estar entre 0 y {item.packageSize} {unit} (el paquete entero).
                                        </small>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Nota</label>
                                    <input type="text" name="adjustNotes" className="form-control" value={this.state.adjustNotes} onChange={this.handleChange} placeholder="ej: conteo físico / merma" />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" disabled={this.state.adjustSaving} onClick={() => this.setState({ adjustItem: null })}>Cancelar</button>
                                <button type="button" className="btn btn-accent" disabled={this.state.adjustSaving} onClick={this.saveAdjust}>Guardar ajuste</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    drawRestockModal = () => {
        if (!this.state.restockOpen) {
            return null;
        }
        const { items, restockLines } = this.state;
        // Filtro por nombre + categoría para encontrar rápido el insumo a reponer.
        const nameQ = this.state.restockFilterName.trim().toLowerCase();
        const catQ = this.state.restockFilterCategory;
        const visibleItems = items.filter((it) => (
            (catQ === '' || it.category === catQ)
            && (nameQ === '' || (it.name || '').toLowerCase().includes(nameQ))
        ));
        // Solo categorías presentes en los insumos (no ofrecer vacías).
        const categories = [...new Set(items.map((it) => it.category).filter(Boolean))];
        const selectedCount = Object.keys(restockLines).length;
        return (
            <div>
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reposición de insumos</h5>
                                <button type="button" className="close" disabled={this.state.restockSaving} onClick={() => this.setState({ restockOpen: false })}><span>&times;</span></button>
                            </div>
                            {/* maxHeight + overflow: con muchos insumos la tabla no empuja el footer fuera de
                                pantalla; el body scrollea y los botones quedan siempre visibles (patrón de StockTransferComponent). */}
                            <div className="modal-body" style={{ maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Proveedor *</label>
                                            <select name="restockProvider" className="custom-select" value={this.state.restockProvider} onChange={this.handleChange}>
                                                <option value="">Elegir…</option>
                                                {this.state.providers.map((p) => (
                                                    <option key={p.providerId} value={p.providerId}>{p.name}</option>
                                                ))}
                                            </select>
                                            {/* ENI-187: el listado ahora es estricto (solo categoría cafetería). Si todavía
                                                no hay ninguno etiquetado, un desplegable vacío y mudo se lee como "está roto":
                                                hay que decir qué falta y dónde se arregla. */}
                                            {this.state.providers.length === 0 && (
                                                <small className="form-text text-danger">
                                                    No hay proveedores de cafetería. Etiquetalos como “Cafetería” en Proveedores para que aparezcan acá.
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Factura (ID) *</label>
                                            <input type="text" name="restockInvoice" className="form-control" value={this.state.restockInvoice} onChange={this.handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Total factura *</label>
                                            <input type="number" step="0.01" min="0" name="restockInvoiceTotal" className="form-control" value={this.state.restockInvoiceTotal} onChange={this.handleChange} />
                                        </div>
                                    </div>
                                </div>
                                {/* Filtros de la lista (nombre + categoría) para encontrar el insumo a reponer. */}
                                <div className="row align-items-end">
                                    <div className="col-md-6">
                                        <div className="form-group mb-2">
                                            <label className="mb-1"><small className="text-muted">Buscar insumo</small></label>
                                            <input type="text" name="restockFilterName" className="form-control form-control-sm" placeholder="Filtrar por nombre…" value={this.state.restockFilterName} onChange={this.handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group mb-2">
                                            <label className="mb-1"><small className="text-muted">Categoría</small></label>
                                            <select name="restockFilterCategory" className="custom-select custom-select-sm" value={this.state.restockFilterCategory} onChange={this.handleChange}>
                                                <option value="">Todas</option>
                                                {categories.map((c) => (<option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-2 text-md-right">
                                        {selectedCount > 0 && (<small className="text-muted">{selectedCount} seleccionado{selectedCount === 1 ? '' : 's'}</small>)}
                                    </div>
                                </div>
                                <table className="table mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th></th>
                                            <th>Insumo</th>
                                            <th className="text-center">Cerrados hoy</th>
                                            <th style={{ width: '120px' }}>Cantidad</th>
                                            <th style={{ width: '140px' }}>Costo unitario</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleItems.map((item) => {
                                            const line = restockLines[item.code];
                                            return (
                                                <tr key={item.code}>
                                                    <td><input type="checkbox" checked={!!line} onChange={() => this.toggleRestockLine(item)} /></td>
                                                    <td>{item.name}</td>
                                                    <td className="text-center">{item.stock}</td>
                                                    <td>{line ? (<input type="number" className="form-control form-control-sm" min="1" value={line.quantity} onChange={(e) => this.updateRestockLine(item.code, 'quantity', e.target.value)} />) : null}</td>
                                                    <td>{line ? (<input type="number" className="form-control form-control-sm" min="0" value={line.cost} onChange={(e) => this.updateRestockLine(item.code, 'cost', e.target.value)} />) : null}</td>
                                                </tr>
                                            );
                                        })}
                                        {visibleItems.length === 0 && (
                                            <tr><td colSpan="5" className="text-center text-muted py-3">Ningún insumo coincide con el filtro.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" disabled={this.state.restockSaving} onClick={() => this.setState({ restockOpen: false })}>Cancelar</button>
                                <button type="button" className="btn btn-accent" disabled={this.state.restockSaving} onClick={this.saveRestock}>Registrar reposición</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    drawMovementsModal = () => {
        const item = this.state.movementsItem;
        if (!item) {
            return null;
        }
        const unit = UNIT_LABELS[item.baseUnit] || item.baseUnit;
        // ENI-193 agrega 'reversal': sin su etiqueta el modal mostraba el enum crudo en inglés.
        const typeLabel = { consumption: 'Consumo', adjustment: 'Ajuste', restock: 'Reposición', reversal: 'Reintegro por anulación' };
        return (
            <div>
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Movimientos — {item.name}</h5>
                                <button type="button" className="close" onClick={() => this.setState({ movementsItem: null })}><span>&times;</span></button>
                            </div>
                            <div className="modal-body p-0">
                                <table className="table mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Tipo</th>
                                            <th className="text-right">Cantidad ({unit})</th>
                                            <th className="text-right">Paquetes</th>
                                            <th>Venta</th>
                                            <th>Nota</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.movementsLoading && (<tr><td colSpan="6">Cargando…</td></tr>)}
                                        {!this.state.movementsLoading && this.state.movements.length === 0 && (
                                            <tr><td colSpan="6" className="text-muted">Sin movimientos.</td></tr>
                                        )}
                                        {this.state.movements.map((m) => (
                                            <tr key={m.id}>
                                                <td>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</td>
                                                <td>{typeLabel[m.type] || m.type}</td>
                                                {/* ENI-190: un 0 en cualquiera de las dos columnas significa "este movimiento
                                                    no tocó ese nivel de stock" (p. ej. una reposición sólo suma paquetes
                                                    cerrados). Se muestra '—' en las dos, con la misma convención. */}
                                                <td className="text-right">{Number(m.quantity) !== 0 ? Number(m.quantity) : '—'}</td>
                                                <td className="text-right">{Number(m.packages) !== 0 ? m.packages : '—'}</td>
                                                <td>{m.purchase_id ? `#${m.purchase_id}` : ''}</td>
                                                <td>{m.notes || ''}{m.user_id ? ` (${m.user_id})` : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ movementsItem: null })}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const filtered = this.state.categoryFilter === ''
            ? this.state.items
            : this.state.items.filter((i) => i.category === this.state.categoryFilter);
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={29} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Cafetería" subtitle="Insumos" />
                            <div className="card card-small mb-4">
                                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                                    <select name="categoryFilter" className="custom-select" style={{ maxWidth: '220px' }} value={this.state.categoryFilter} onChange={this.handleChange}>
                                        <option value="">Todas las categorías</option>
                                        {Object.keys(CATEGORY_LABELS).map((c) => (<option key={c} value={c}>{CATEGORY_LABELS[c]}</option>))}
                                    </select>
                                    <div>
                                        <button className="btn btn-outline-primary mr-2" onClick={this.openRestock}>Reponer</button>
                                        <button className="btn btn-accent" onClick={() => navigateTo('/addCafeteriaInsumo')}>Agregar insumo</button>
                                    </div>
                                </div>
                                <div className="card-body p-0">
                                    <table className="table mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Categoría</th>
                                                <th className="text-center">Cerrados</th>
                                                <th className="text-center">Paquete en uso</th>
                                                <th className="text-center">Disponible</th>
                                                <th className="text-center">Avisos</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.state.loading && (<tr><td colSpan="7">Cargando…</td></tr>)}
                                            {!this.state.loading && filtered.length === 0 && (
                                                <tr><td colSpan="7" className="text-muted">No hay insumos{this.state.categoryFilter ? ' en esta categoría' : ''}.</td></tr>
                                            )}
                                            {filtered.map(this.drawRow)}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                {this.drawAdjustModal()}
                {this.drawRestockModal()}
                {this.drawMovementsModal()}
            </div>
        );
    }
}

export default CafeteriaInsumosPage;
